"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, CircleCheck as CheckCircle2, Clock, Building2, TrendingUp, PhoneCall, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const highlights = [{
  icon: TrendingUp,
  title: "Data-Driven Revenue Strategy",
  description: "Uncover hidden revenue opportunities across major OTAs and direct channels."
}, {
  icon: Clock,
  title: "30-Minute Expert Audit",
  description: "Get direct actionable feedback from senior revenue strategist Sunil Singh."
}, {
  icon: CheckCircle2,
  title: "Tailored Tech Stack Options",
  description: "Streamline distribution with PMS, Channel Manager, and RMS recommendations."
}];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center bg-accent text-accent-foreground rounded-xl space-y-4">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/20 text-primary mb-2">
          <CheckCircle2 className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-bold font-default">{successMessage}</h3>
        <p className="text-sm opacity-90">
          Sunil Singh will review your hotel details and confirm your consultation schedule shortly.
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Book Another Session
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Sunil Singh" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="sunil@hotelrevbaba.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 019-2834" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="hotelName" rules={{
        required: "Property name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Hotel / Property Name</FormLabel>
              <FormControl>
                <Input placeholder="Grand Horizon Resort" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="notes" render={({
      field
    }) => <FormItem>
            <FormLabel>Primary Goal or Challenge (Optional)</FormLabel>
            <FormControl>
              <Textarea placeholder="Share your current room count, OTA challenges, or primary revenue goals..." rows={3} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 text-destructive border border-destructive/20 rounded-md text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base py-6 shadow-md">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Scheduling Your Audit...
          </> : <>
            <SafeHydrate><Calendar className="mr-2 size-5" /></SafeHydrate>
            Book Your Free Consultation
          </>}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-2">
        Zero pressure. Strict confidentiality guaranteed for all financial &amp; occupancy metrics.
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="consultationcta" data-nav="dark" className="bg-secondary text-secondary-foreground py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {}
          <div className="lg:col-span-6 space-y-8">
            <motion.div variants={itemVariants} className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider">
                <PhoneCall className="size-3.5" />
                <span>Strategy Session</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default leading-tight text-secondary-foreground">
                Maximize Your Hotel’s Revenue with Data-Driven OTA Strategies
              </h2>
              <p className="text-secondary-foreground/80 text-base sm:text-lg leading-relaxed">
                Schedule a direct consultation with revenue expert Sunil Singh. Discover actionable tactics to optimize pricing, boost OTA placement, and drive bottom-line profitability.
              </p>
            </motion.div>

            {}
            <div className="space-y-4">
              {highlights.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.title} data-index={i} variants={itemVariants} className="flex items-start gap-4 p-4 rounded-xl bg-secondary-foreground/5 border border-secondary-foreground/10">
                    <div className="p-2.5 rounded-lg bg-primary text-primary-foreground shrink-0 mt-0.5">
                      <Icon className="size-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-secondary-foreground font-default">
                        {item.title}
                      </h3>
                      <p className="text-sm text-secondary-foreground/70 mt-0.5">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>

            {}
            <motion.div variants={itemVariants} className="pt-2 flex items-center gap-6 border-t border-secondary-foreground/10 text-xs text-secondary-foreground/70">
              <div className="flex items-center gap-2">
                <Building2 className="size-4 text-primary" />
                <span>Individual Hotels &amp; Resorts</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary" />
                <span>No Long-Term Contracts</span>
              </div>
            </motion.div>
          </div>

          {}
          <motion.div variants={itemVariants} className="lg:col-span-6">
            <Card className="bg-card text-card-foreground shadow-2xl border border-border">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold font-default">
                  Book a Consultation
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Fill in your details below to request a complimentary 30-minute revenue assessment session.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <FormProvider formId="consultation-booking-cta" sectionName="consultation-cta" defaultValues={{
                fullName: "",
                email: "",
                phone: "",
                hotelName: "",
                notes: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                hotelName: {
                  required: true
                }
              }} submitText="Book Your Free Consultation" successMessage="Consultation request received!">
                  <ConsultationFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}